import * as people from "@/api/people";
import { getIsAccessDisabled } from "@/utils/permissions";
import {
  REDEEM_PHONE,
  REDEEM_EMAIL,
  REDEEM_EMAIL_AND_PHONE,
} from "@/views/search/constants";
import {
  withoutUnsupportedJobLocationOperators,
} from "@/views/search/search-common/location-filters";

const getInitialFilterState = () => ({
  search_term: "",
  people_groups: [],
  people_name: "",
  people_titles: [],
  departments: [],
  department_functions: [],
  management_levels: [],
  employees: [],
  verticals: [],
  vertical_categories: [],
  vertical_sub_categories: [],
  people_locations: new Map(),
  company_locations: new Map(),
  companies: [],
  technologies: [],
  categories: [],
  sequences: [],
  search_modes: ["real_time"],
  page: 1,
  notReset: false,
  // is_trigger_error: true,
  per_page: parseInt(process.env.VUE_APP_PER_PAGE),

  social_media: [],
  founded_dates: [],
  revenues: [],
  job_locations: new Map(),
  job_exclude_locations: new Map(),
  job_posted_dates: [],
  job_titles: [],
  exclude_job_titles: [],
  keywords: [],
  news_categories: [],
  news_published_dates: [],
  advertisement_target_locations: new Map(),
  advertisement_exclude_target_locations: new Map(),
  advertisement_search_terms: [],
  advertisement_start_dates: [],
  advertisement_end_dates: [],

  places: [],
  exclude_places: [],

  filter_conditions: [
    {
      key: "technologies",
      operator: "or",
    },
    {
      key: "categories",
      operator: "or",
    },
    {
      key: "verticals",
      operator: "or",
    },
    {
      key: "vertical_categories",
      operator: "or",
    },
    {
      key: "vertical_sub_categories",
      operator: "or",
    },
    {
      key: "keywords",
      operator: "or",
    },
    {
      key: "places",
      operator: "or",
    },
    {
      key: "exclude_places",
      operator: "or",
    },
    {
      key: "social_media",
      operator: "or",
    },
  ],
});

const state = {
  selected: getInitialFilterState(),
  checkedList: [],
  isLoading: false,
  isShow: true,
  lists: [],
  total: {},
  peopleExportFields: [],
  multipleSelectedListIds: [],
  savedList: {},
  sequenceList: {},
  isRealTimeMode: true,
};

const getters = {
  apiPayload: (state, getters, rootState) => {
    const { selected } = state;

    const payload = {
      ...selected,
      profile_id: rootState.user.currentId.profile_id,
      people_groups: selected?.people_groups?.map((i) => i.value),
      departments: selected.departments.map((i) => i.value),
      department_functions: selected.department_functions.map((i) => i.value),
      management_levels: selected.management_levels.map((i) => i.value),
      verticals: selected.verticals.map((i) => i.value),
      vertical_categories: selected.vertical_categories.map((i) => i.value),
      vertical_sub_categories: selected.vertical_sub_categories.map(
        (i) => i.value,
      ),
      people_locations: selected.people_locations
        ? [...selected.people_locations.keys()]
        : [],
      company_locations: selected.company_locations
        ? [...selected.company_locations.keys()]
        : [],
      technologies: selected.technologies.map((i) => i.value),
      categories: selected.categories.map((i) => i.value),
      sequences: selected.sequences.map((i) => i.value),
      employees: selected.employees,
      page: selected.notReset ? selected.page : 1,

      job_locations: [...selected.job_locations.keys()],
      job_exclude_locations: [...selected.job_exclude_locations.keys()],
      news_categories: selected.news_categories?.map((item) => item.value),
      social_media: selected.social_media?.map((item) => item.value),
      advertisement_target_locations: [
        ...selected.advertisement_target_locations.keys(),
      ],
      advertisement_exclude_target_locations: [
        ...selected.advertisement_exclude_target_locations.keys(),
      ],
      places: selected.exclude_places?.length ? [] : selected.places,
      exclude_places: selected.exclude_places,
      filter_conditions: withoutUnsupportedJobLocationOperators(
        selected.filter_conditions,
      ),
    };

    delete payload.notReset;
    return payload;
  },
};

const mutations = {
  SET_FILTERS_FROM_PAYLOAD(state, payload) {
    state.selected = {
      ...getInitialFilterState(),
      ...state.selected,
      ...payload,
    };
  },

  UPDATE_FILTER(state, { dataKey, value }) {
    if (Object.prototype.hasOwnProperty.call(state.selected, dataKey)) {
      state.selected[dataKey] = value;
      state.selected.page = 1;
      state.selected = { ...state.selected };
    }
  },

  UPSERT_TO_MAP_FILTER(state, { dataKey, item }) {
    if (state.selected[dataKey] instanceof Map) {
      state.selected[dataKey].set(item.value, item);
      state.selected = { ...state.selected };
    }
  },

  ADD_TO_ARRAY_FILTER(state, { dataKey, item }) {
    if (Array.isArray(state.selected[dataKey])) {
      const list = state.selected[dataKey];
      const exists = list.some((i) => i.value === item.value);
      if (!exists) {
        list.push(item);
        state.selected = { ...state.selected };
      }
    }
  },

  REMOVE_FROM_MAP_FILTER(state, { dataKey, itemValue }) {
    if (state.selected[dataKey] instanceof Map) {
      if (state.selected[dataKey].delete(itemValue)) {
        state.selected = { ...state.selected };
      }
    }
  },

  REMOVE_FROM_ARRAY_FILTER(state, { dataKey, itemValue }) {
    if (Array.isArray(state.selected[dataKey])) {
      const list = state.selected[dataKey];
      const index = list.findIndex((i) => i.value === itemValue);
      if (index > -1) {
        list.splice(index, 1);
        state.selected = { ...state.selected };
      }
    }
  },

  CLEAR_COMPOSITE_FILTER(state, dataKey) {
    if (dataKey === "industry") {
      state.selected.verticals = [];
      state.selected.vertical_categories = [];
      state.selected.vertical_sub_categories = [];
    } else if (dataKey === "technology") {
      state.selected.technologies = [];
      state.selected.categories = [];
    } else if (dataKey === "companyAndPeopleLocations") {
      state.selected.company_locations = new Map();
      state.selected.people_locations = new Map();
    } else if (dataKey === "news") {
      state.selected.news_categories = [];
      state.selected.news_published_dates = [];
    } else if (dataKey === "jobs") {
      state.selected.job_locations = new Map();
      state.selected.job_exclude_locations = new Map();
      state.selected.job_posted_dates = [];
      state.selected.job_titles = [];
      state.selected.exclude_job_titles = [];
    } else if (dataKey === "advertisement") {
      state.selected.advertisement_target_locations = new Map();
      state.selected.advertisement_exclude_target_locations = new Map();
      state.selected.advertisement_search_terms = [];
      state.selected.advertisement_start_dates = [];
      state.selected.advertisement_end_dates = [];
    } else if (dataKey === "places") {
      state.selected.places = [];
      state.selected.exclude_places = [];
    }
    state.selected = { ...state.selected };
  },

  RESET_ALL_FILTERS(state) {
    state.selected = {
      ...getInitialFilterState(),
      companies: state.selected.companies,
    };
  },

  setRealTimeMode(state, payload) {
    state.isRealTimeMode = payload;
  },

  setCollapse(state) {
    state.isShow = !state.isShow;
  },
  setLoading(state, val) {
    state.isLoading = val;
  },
  setCheckedList(state, payload) {
    state.checkedList = payload;
  },
  setSavedList(state, payload) {
    state.savedList = payload;
  },
  setSequenceList(state, payload) {
    state.sequenceList = payload;
  },
  setMultiAddSavedList(state, addedGroupInfo) {
    const ids = state.checkedList.map((item) => item.people_search_id);
    state.lists = state.lists.map((item) => {
      if (ids.includes(item.people_search_id)) {
        if (item.saved_lists) {
          const groupIds = item.saved_lists.map((group) => group.group_id);
          return groupIds.includes(addedGroupInfo.group_id)
            ? item
            : {
                ...item,
                saved_lists: [...item.saved_lists, addedGroupInfo],
              };
        } else {
          return {
            ...item,
            saved_lists: [addedGroupInfo],
          };
        }
      } else {
        return item;
      }
    });
  },
  setRowAddSavedList(state, payload) {
    const { people_search_id, group } = payload;
    state.lists = state.lists.map((item) => {
      if (item.people_search_id === people_search_id) {
        if (item.saved_lists) {
          const groupIds = item.saved_lists.map(
            (groupItem) => groupItem.group_id,
          );
          return groupIds.includes(group.group_id)
            ? item
            : {
                ...item,
                saved_lists: [...item.saved_lists, group],
              };
        } else {
          return {
            ...item,
            saved_lists: [group],
          };
        }
      } else {
        return item;
      }
    });
  },
  setMultiDeleteSavedList(state, groupInfo) {
    const ids = state.checkedList.map((item) => item.people_search_id);
    state.lists = state.lists.map((item) => {
      return ids.includes(item.people_search_id)
        ? {
            ...item,
            saved_lists: item.saved_lists.filter(
              (groupItem) => groupItem.group_id !== groupInfo.group_id,
            ),
          }
        : item;
    });
  },
  setRowDeleteSavedList(state, payload) {
    const { people_search_id, group } = payload;
    state.lists = state.lists.map((item) => {
      if (item.people_search_id === people_search_id) {
        return {
          ...item,
          saved_lists: item.saved_lists.filter(
            (groupItem) => groupItem.group_id !== group.group_id,
          ),
        };
      } else {
        return item;
      }
    });
  },
  setSearchPeopleList(state, payload) {
    if (payload.data) {
      payload.data.peoples &&
        (state.lists = payload.data.peoples?.map((item) => ({
          ...item,
          loading: false,
        })));
      payload.data.pagination && (state.total = payload.data.pagination);
    }
  },
  setPeopleExportFields(state, payload) {
    state.peopleExportFields = payload;
  },
  setMultipleSelectedListIds(state, payload) {
    state.multipleSelectedListIds = payload;
  },
  updateContact(state, { people_search_id, data, contactType }) {
    const index = state.lists.findIndex(
      (p) => p.people_search_id === people_search_id,
    );
    if (index !== -1) {
      const contacts = [
        ...(state.lists[index].contacts || []).filter(
          (c) =>
            !c.type.includes(contactType === REDEEM_EMAIL ? "email" : "phone"),
        ),
        ...data.map((e) => ({ ...e, is_redeemed: true })),
      ];
      const typeParams =
        contactType === REDEEM_EMAIL
          ? { redeemEmail: true }
          : { redeemPhone: true };
      state.lists = [
        ...state.lists.slice(0, index),
        {
          ...state.lists[index],
          contacts,
          ...typeParams,
        },
        ...state.lists.slice(index + 1),
      ];
    }
  },
  updateMultipleContacts(state, { data, type }) {
    let typeList = [];
    if (type === REDEEM_EMAIL) {
      typeList = ["phone"];
    } else if (type === REDEEM_PHONE) {
      typeList = ["email-personal", "email-work", "email-generic"];
    }

    state.lists = state.lists.map((item) => {
      const matchingData = data.find(
        (child) => item.people_search_id === child.people_search_id,
      );

      if (matchingData) {
        const { emails, phones } = matchingData;
        const updatedContacts = [];

        if (type === REDEEM_EMAIL || type === REDEEM_EMAIL_AND_PHONE) {
          updatedContacts.push(
            ...(emails
              ? emails.map((email) => ({
                  ...email,
                  is_redeemed: true,
                }))
              : []),
          );
        }

        if (type === REDEEM_PHONE || type === REDEEM_EMAIL_AND_PHONE) {
          updatedContacts.push(
            ...(phones
              ? phones.map((phone) => ({
                  ...phone,
                  is_redeemed: true,
                }))
              : []),
          );
        }

        return {
          ...item,
          contacts: [
            ...(item.contacts ?? []).filter((contactItem) =>
              typeList.includes(contactItem.type),
            ),
            ...updatedContacts,
          ],
          redeemEmail: item.redeemEmail
            ? item.redeemEmail
            : type === REDEEM_EMAIL || type === REDEEM_EMAIL_AND_PHONE,
          redeemPhone: item.redeemPhone
            ? item.redeemPhone
            : type === REDEEM_PHONE || type === REDEEM_EMAIL_AND_PHONE,
        };
      }

      return item;
    });
  },
  updateMultipleSequence(state, { type, sequenceInfo, peopleList }) {
    state.lists = state.lists.map((item) => {
      if (peopleList.includes(item.people_search_id)) {
        let sequences = item.sequences || [];
        if (type === "add") {
          const alreadyHasSequenceItem = sequences.find(
            (child) =>
              child.sequence_identifier === sequenceInfo.sequence_identifier,
          );
          if (!alreadyHasSequenceItem) {
            sequences = [...sequences, sequenceInfo];
          }
        } else {
          sequences = sequences.filter(
            (child) =>
              child.sequence_identifier !== sequenceInfo.sequence_identifier,
          );
        }
        return { ...item, sequences };
      }
      return item;
    });
  },
};
const actions = {
  updateFilter({ commit }, payload) {
    // payload: { dataKey, value }
    commit("UPDATE_FILTER", payload);
  },

  clearFilterGroup({ commit }, dataKey) {
    if (
      [
        "industry",
        "technology",
        "news",
        "jobs",
        "companyAndPeopleLocations",
        "advertisement",
      ].includes(dataKey)
    ) {
      commit("CLEAR_COMPOSITE_FILTER", dataKey);
    } else {
      const initialValue = getInitialFilterState()[dataKey];
      commit("UPDATE_FILTER", { dataKey, value: initialValue });
    }
  },

  clearAllFilters({ commit }) {
    commit("RESET_ALL_FILTERS");
  },
  async searchPeopleList(context, val) {
    try {
      if (
        getIsAccessDisabled(
          context.rootState?.people?.people_search_list_access_code,
        )
      ) {
        return;
      }
      context.commit("setLoading", true);
      const result = await people.searchPeopleList(val);
      context.commit("setLoading", false);
      if (result && result.data) {
        context.commit("setSearchPeopleList", result);
        return result?.data;
      }
    } catch (error) {
      const code = error?.response?.data?.code;

      context.commit("people/set_people_search_list_access_code", +code, {
        root: true,
      });

      context.commit("setLoading", false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
